import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {User} from '@models/user/user';
import {DashboardMachine} from '@models/dashboard/dashboard-machine';
import {DATA_TYPE} from '@constants/data-type.constant';

import {TranslateService} from '@ngx-translate/core';
import {MachineDetailsService} from '@services/dashboard/machine-details.service';
import {Gateway} from '@models/gateway/gateway';
import {GatewayStatusService} from '@services/gateway-status/gateway-status.service';
import {GATEWAYS_AGGREGATED_STATUS} from '@constants/machine/gateways-aggregated-status';
import {RealtimePollingService} from '@services/realtime-polling/realtime-polling.service';
import {RealtimePollingRequestParameters} from '@services/realtime-polling/realtime-polling-request-parameters';
import {GATEWAY_STATUS} from '@constants/gateway/gateway-status';
import {EnvironmentConfig} from '@environments/environment';
import {MachineLayoutComponent} from '@components/dashboard/tiles/machine-layout/machine-layout.component';
import {DashboardConfiguration} from '@models/dashboard/dashboard-configuration';
import {DashboardSignalItem} from '@models/dashboard/dashboard-signal-item';
import {DateService} from '@services/date.service';
import {DecimalPipe} from '@angular/common';
import {cloneDeep} from 'lodash';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {MenuItem} from '@models/sidebar/menu-item';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {TRAFFIC_LIGHT_STATUS_CLASS} from '@constants/machine/traffic-light/traffic-light-status';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {MenuItemIcon} from '@models/sidebar/menu-item-icon';
import {TranslationService} from '@services/translation/translation.service';
import {
  StatusEventSignalDataHelperService
} from '@services/status-event-signal-data/status-event-signal-data-helper.service';
import {MachineTileValueItemDetails} from '@models/dashboard/machine-tile/machine-tile-value-item-details';
import {MachineCategoryService} from '@services/administration/portal-setup/machine-category/machine-category.service';
import {DashboardService} from '@services/dashboard/dashboard.service';
import {
  MachineCategoryColumnConfiguration
} from '@models/adminstration/portal-setup/machine-category/machine-category-column-configuration';
import {catchError, switchMap, takeUntil} from 'rxjs/operators';
import {Observable, of, Subject, throwError} from 'rxjs';
import {MACHINE_STATUS} from '@constants/machine/machine-status';
import {ModalService} from '@services/modal/modal.service';
import {MachineService} from '@services/administration/machine/machine.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {SettingsService} from '@services/administration/settings/settings.service';
import {utob} from '@prototypes/global.functions';

@Component({
    selector: 'scitis-dashboard-machine',
    templateUrl: './machine.component.html',
    styleUrls: ['./machine.component.scss'],
    standalone: false
})
export class MachineComponent implements OnInit, OnDestroy, OnChanges {

  @Input()
  public machine: DashboardMachine | any;
  @Input()
  public isAnalyticsRole: boolean | undefined;
  @Input()
  public isViewerRole: boolean | undefined;
  @Input()
  public isAdminRole: boolean | undefined;
  @Input()
  public isSuperAdminRole: boolean | undefined;
  @Input()
  public isWindowContentVisible: boolean | undefined;
  @Input()
  public currentUser: User | undefined;
  @Input()
  public refreshInterval: number = -1;

  public configuration: DashboardConfiguration = new DashboardConfiguration();
  @Input()
  public availableMachineCategories: MachineCategoryColumnConfiguration[] = [];

  @Input()
  public currentMachineGroupId: string = '';

  public GatewayStatusEnum = GATEWAY_STATUS;
  public GatewayAggregatedStatusEnum = GATEWAYS_AGGREGATED_STATUS;
  public MachineStatusEnum = MACHINE_STATUS;
  public computing: boolean = false;
  public machineName: string = '';
  public machineType: string = '';
  public machineIcon: string = '';
  public gatewaysStatusClass: string = 'btn-outline-dark';
  public gatewaysStatus: GATEWAYS_AGGREGATED_STATUS = GATEWAYS_AGGREGATED_STATUS.DISCONNECTED;
  public gateways: any[] = [];
  public placeholderImage: string | undefined;
  public machineStatus: MACHINE_STATUS = MACHINE_STATUS.UNKNOWN;
  public gatewaysConnectorsActive: boolean = true;
  public showDate: boolean = true;
  public statusChangeTimestamp: number = 0;
  public trafficLightClass: string = TRAFFIC_LIGHT_STATUS_CLASS.CANNOT_EVALUATE;
  public loadingMenuItem = false;
  public displayedSignalsValues: MachineTileValueItemDetails[][] = [];
  public NONE_SIGNAL_NAME: string = 'none';
  public tileHasNoSignalsConfigured: boolean = false;
  public categoryDisplayName: string = '';
  public MACHINE_STATUS_UNAVAILABLE = [MACHINE_STATUS.DISCONNECTED, MACHINE_STATUS.OFFLINE, MACHINE_STATUS.UNKNOWN, MACHINE_STATUS.CANNOTEVALUATE];

  private GATEWAYS_AGGREGATED_UNAVAILABLE = [GATEWAYS_AGGREGATED_STATUS.DISCONNECTED, GATEWAYS_AGGREGATED_STATUS.NO_GATEWAYS, GATEWAYS_AGGREGATED_STATUS.MISCONFIGURATION, GATEWAYS_AGGREGATED_STATUS.PARTIALLY_DISCONNECTED];
  private MAX_ROWS_COUNT: number = 4;
  private MAX_COLUMNS_COUNT: number = 2;
  private MACHINE_DATA_URL_SUFFIX: string = '/api/web/tenant/:tenant/machine/:machine/tile?refresh=:refresh&sampling=:sampling';
  private MACHINE_STATUS_SIGNAL_NAME: string = 'MachineStatus';
  private NOT_AVAILABLE: string = 'N/A';
  private SAMPLING_RATE = EnvironmentConfig.PortalSettings.tileSamplingInterval;
  private REFRESH_INTERVAL = EnvironmentConfig.PortalSettings.tileRefreshInterval;
  private trafficLight: any;
  private machineTileIsInView: boolean = false;
  private portalLanguage: string = '';
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private readonly COMPONENT_NAME: string = 'DashboardMachineComponent';

  constructor(private modalService: ModalService,
              private dateService: DateService,
              private decimalPipe: DecimalPipe,
              private translate: TranslateService,
              private pollingService: RealtimePollingService,
              private machineDetailsService: MachineDetailsService,
              private gatewayStatusService: GatewayStatusService,
              private menuNotificationService: MenuNotificationService,
              private machineCategoryService: MachineCategoryService,
              private translationService: TranslationService,
              private statusEventSignalDataHelperService: StatusEventSignalDataHelperService,
              private machineService: MachineService,
              private loadingService: LoadingIndicatorService,
              private settingsService: SettingsService,
              private dashboardService: DashboardService) {

  }

  public get isInputValueMissing() {
    return (MACHINE_STATUS.UNKNOWN === this.machineStatus || !this.hasValuesToDisplay) && this.gatewaysConnectorsActive
        && this.GATEWAYS_AGGREGATED_UNAVAILABLE.indexOf(this.gatewaysStatus) < 0;
  }

  public get hasValuesToDisplay(): boolean {
    return this.displayedSignalsValues && this.displayedSignalsValues.length > 0;
  }

  private static isTimestampOlderThanMidnight(timestamp: number): boolean {
    const midNight = new Date();
    midNight.setHours(0, 0, 0, 0);

    const timestampToVerify = new Date(timestamp);
    return timestampToVerify < midNight;
  }

  private static decodeBase64ImageSourceString(image: string): string {
    return utob(image);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.configuration?.currentValue) {
      this.tileHasNoSignalsConfigured = this.determineIfTileHasNoSignalsConfigured();
      if (this.tileHasNoSignalsConfigured) {
        this.stopDataRetrieval();
        this.computing = false;
      }
    }

    if (changes.refreshInterval && this.refreshInterval > 0 && this.machineTileIsInView && this.isWindowContentVisible) {
      this.startDataRetrieval();
    }

    if (changes.isWindowContentVisible) {
      console.log(`Machine.  + ${this.machine.name}  Visibility.  ${this.isWindowContentVisible} `);
      if (this.isWindowContentVisible && this.machineTileIsInView) {
        this.startDataRetrieval();
      } else {
        console.log('Stopping polling mechanism');
        this.stopDataRetrieval();
      }
    }
    if (changes?.availableMachineCategories?.currentValue) {
      this.setCategoryIconAndDisplayName();
    }

  }

  ngOnInit(): void {
    const machineDisplayProperties = this.machineDetailsService.getMachineDisplayProperties(this.machine);
    this.machineName = machineDisplayProperties.machineName;
    this.machineType = machineDisplayProperties.machineType;
    this.setCategoryIconAndDisplayName();
    this.portalLanguage = this.translationService.getActiveLanguage();
    this.subscribe();
  }

  ngOnDestroy() {
    this.machineTileIsInView = false;
    console.log(`Machine ${this.machine.name} destroyed. Polling stopped`);
    this.stopDataRetrieval();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onInViewChangeEvent(event: any) {
    const {visible} = event;
    if (visible && this.isWindowContentVisible) {
      this.machineTileIsInView = true;
      console.log(`Machine ${this.machine.name}. Page visible and tile in view`);
      this.startDataRetrieval();

    } else {
      this.machineTileIsInView = false;
      console.log(`Machine ${this.machine.name}. Tile not view`);
      this.stopDataRetrieval();
    }
  }

  public onEditMachineTile(event: Event) {
    event.stopPropagation();
    const originalConfiguration = cloneDeep(this.configuration);

    this.stopDataRetrieval();
    const modalRef = this.modalService.open(MachineLayoutComponent);
    modalRef.componentInstance.machine = this.machine;
    modalRef.componentInstance.machineIcon = this.machineIcon;
    modalRef.componentInstance.configuration = this.configuration;
    modalRef.componentInstance.isAdminOrSuperAdmin = this.isSuperAdminRole || this.isAdminRole;

    modalRef.result.then(() => {
    }, (reason) => {
      if (reason === ModalDialogDismissOption.CLOSE) {
        this.configuration = originalConfiguration;
      }
      this.tileHasNoSignalsConfigured = this.determineIfTileHasNoSignalsConfigured();
      this.startDataRetrieval();
    });
  }

  public openDefaultItemFromLeftMenu() {
    this.focusOnLeftMenu();
  }

  public onDisableToolTip(): boolean {
    return !this.trafficLightClass || this.trafficLightClass === 'offline';
  }

  public getTooltip(): string {
    return this.translate.instant('Name') + ': ' + this.machineName + '\n'
        + this.translate.instant('Type') + ': ' + this.machineType + '\n'
        + this.translate.instant('Tag') + ': ' + this.machine.machineTag;
  }

  public getTrafficTooltip(): string {
    switch (this.trafficLightClass) {
      case TRAFFIC_LIGHT_STATUS_CLASS.FAILURE:
        return this.translate.instant('Machine malfunction');
      case TRAFFIC_LIGHT_STATUS_CLASS.WARNING:
        return this.translate.instant('Machine Warning or error-free switch off');
      case TRAFFIC_LIGHT_STATUS_CLASS.RUNNING:
        return this.translate.instant('Machine operating faultlessly');
      case TRAFFIC_LIGHT_STATUS_CLASS.INFO:
        return this.translate.instant('Machine info, debug, inhibit events');
      case TRAFFIC_LIGHT_STATUS_CLASS.CANNOT_EVALUATE:
        return this.translate.instant('Machine traffic light cannot be evaluated');
    }
    return '';
  }

  public machineTagExists(): boolean {
    return this.machine.machineTag && this.machine.machineTag.length > 0;
  }

  public onOpenStatusEventPage($event): void {
    this.preventOpeningDefaultItemFromLeftMenu($event);
    const unavailableStatuses = [GATEWAYS_AGGREGATED_STATUS.DISCONNECTED, GATEWAYS_AGGREGATED_STATUS.NO_GATEWAYS, GATEWAYS_AGGREGATED_STATUS.MISCONFIGURATION];

    if (this.gateways && unavailableStatuses.indexOf(this.gatewaysStatus) > -1) {
      this.openDefaultItemFromLeftMenu();
      return;
    }

    if (!this.gatewaysConnectorsActive || !this.trafficLight || !this.trafficLight.status) {
      this.openDefaultItemFromLeftMenu();
      return;
    }

    this.getMachineConfiguration();
  }

  private getMachineConfiguration() {
    this.loadingService.show();
    this.machineService.getMachineConfiguration(this.machine.id).pipe(
        takeUntil(this.destroy$),
        switchMap((res) => {
          if (res.trafficLightConfiguration?.statusEventPageId && res.trafficLightConfiguration?.statusEventPageId > -1) {
            this.loadingMenuItem = true;
            this.machine.trafficLightConfiguration = res.trafficLightConfiguration;
            const id = this.currentMachineGroupId + '_' + this.machine.id;
            return this.getItemMenuItemAndParentMenuItem(id);
          } else {
            return of(null);
          }
        })
    ).subscribe({
      next: (machineMenuItem) => {
        if (machineMenuItem) {
          const statusEventPageMenuItem: MenuItem = this.generateStatusEventPageMenuItem(machineMenuItem);
          this.menuNotificationService.broadcastClickOnDashboard(statusEventPageMenuItem);
        } else {
          this.openDefaultItemFromLeftMenu();
        }
        this.loadingService.hide();
        this.loadingMenuItem = false;
      },
      error: (error) => {
        console.error(`${this.COMPONENT_NAME}. Redirect to machine page has been failed.`, JSON.stringify(error));
        this.loadingService.hide();
        this.loadingMenuItem = false;
      }
    });
  }

  private getItemMenuItemAndParentMenuItem(id): Observable<MenuItem> {
    return this.dashboardService.getItemMenuItemAndParentMenuItem(id, true).pipe(catchError((error) => {
      console.error(`${this.COMPONENT_NAME}. Error getting menu item.`, JSON.stringify(error));
      return throwError(() => error);
    }));
  }

  preventOpeningDefaultItemFromLeftMenu(event: Event): void {
    event.stopPropagation();
  }

  private determineIfTileHasNoSignalsConfigured(): boolean {

    if (!this.configuration || !this.configuration.dashboardRowsMap) {
      return false;
    }
    for (let row = 0; row < this.MAX_ROWS_COUNT; row++) {

      for (let col = 0; col < this.MAX_COLUMNS_COUNT; col++) {
        const rowElement = this.configuration.dashboardRowsMap[row];
        if (rowElement && rowElement.itemsMap && rowElement.itemsMap[col] && rowElement.itemsMap[col].signalName != this.NONE_SIGNAL_NAME) {
          return false;
        }

      }
    }
    return true;

  }

  private getConfigDetails(configuration: DashboardConfiguration, row: number, col: number): DashboardSignalItem {
    const defaultConfiguration: DashboardSignalItem = {
      signalName: this.NONE_SIGNAL_NAME,
      decimals: 0,
      value: '',
      label: this.NONE_SIGNAL_NAME,
      scale: 0,
      unit: '',
      type: '',
      isByteArray: false,
      isStatusSignal: false
    };

    if (!configuration || !configuration.dashboardRowsMap || !configuration.dashboardRowsMap[row] ||
        !configuration.dashboardRowsMap[row].itemsMap || !configuration.dashboardRowsMap[row].itemsMap[col]) {
      return defaultConfiguration;
    }
    return configuration.dashboardRowsMap[row].itemsMap[col];
  }

  private parseValues(values: any): MachineTileValueItemDetails[][] {
    if (!this.machine || !values || !values.dashboardRowsMap) {
      return [];
    }

    const result: MachineTileValueItemDetails[][] = [];

    for (let row = 0; row < this.MAX_ROWS_COUNT; row++) {
      const rowItem: MachineTileValueItemDetails[] = [];
      for (let col = 0; col < this.MAX_COLUMNS_COUNT; col++) {
        const item = new MachineTileValueItemDetails();
        if (!values.dashboardRowsMap[row] || !values.dashboardRowsMap[row].itemsMap[col]) {
          rowItem.push(item);
          continue;
        }

        const configuration: DashboardSignalItem = this.getConfigDetails(this.configuration, row, col);
        const element: DashboardSignalItem = values.dashboardRowsMap[row].itemsMap[col];
        item.signalName = element.signalName;
        item.label = this.getDisplayedLabel(element, configuration);

        const jsonObject = this.statusEventSignalDataHelperService.tryParseJSONObject(element.value);
        if (jsonObject != null) {
          element.value = jsonObject.originalValue;
          item.enhancedDetailsBody = this.statusEventSignalDataHelperService.getEnhancedValue(this.portalLanguage, jsonObject);
        }
        item.value = this.getDisplayedValueByType(element, configuration);

        item.hasEnhancedDetails = !!item.enhancedDetailsBody;
        item.enhancedDetailsTitle = item.label + ' : ' + item.value;
        item.isValueMissing = element.signalName !== this.NONE_SIGNAL_NAME && (!element.value || element.value === this.NOT_AVAILABLE);

        rowItem.push(item);
      }
      result.push(rowItem);
    }
    return result;
  }

  private getDisplayedValueByType(element: DashboardSignalItem, config: DashboardSignalItem): string {

    const signalName: string = element.signalName;
    if (!signalName || signalName === this.NONE_SIGNAL_NAME) {
      return '';
    }

    if (signalName === this.MACHINE_STATUS_SIGNAL_NAME) {
      return this.displayMachineStatusInformation();
    }
    let type = config.type;
    const decimals = config.decimals | 0;

    let value: string = element.value;
    let finalValue: string;

    if (config.isByteArray) {
      type = DATA_TYPE.STRING;
    }

    if (config.isStatusSignal) {
      type = DATA_TYPE.STRING;
    }

    switch (type) {
      case DATA_TYPE.TIMESTAMP: {
        finalValue = String(this.dateService.getMediumTime(value));
        break;
      }
      case DATA_TYPE.STRING:
      case DATA_TYPE.BYTES: {
        finalValue = value;
        break;
      }
      case DATA_TYPE.BOOL:
      case DATA_TYPE.BOOLEAN: {
        finalValue = value;
        break;
      }
      default: {
        if (value !== this.NOT_AVAILABLE) {
          value = config.scale === 0 && (type === DATA_TYPE.INT16 || type === DATA_TYPE.INT32 || type === DATA_TYPE.INT64 || type === DATA_TYPE.INTEGER)
              ? Number(value).toLocaleString('fullwide', {useGrouping: false, maximumFractionDigits: 0})
              : String(this.decimalPipe.transform(value, `1.${decimals}-${decimals}`, this.translationService.getActiveLanguage()));
        }
        finalValue = value;
        break;
      }
    }
    return finalValue;
  }


  private getDisplayedLabel(element: Pick<DashboardSignalItem, 'signalName'>, config: DashboardSignalItem): string {

    const dashboardSignalName = element.signalName;
    if (!dashboardSignalName) {
      element.signalName = this.NONE_SIGNAL_NAME;
      return '';
    }

    if (dashboardSignalName === this.NONE_SIGNAL_NAME) {
      return '';
    }

    if (dashboardSignalName === this.MACHINE_STATUS_SIGNAL_NAME) {
      return this.translateMachineStatusIntoDescriptionLabel();
    }

    const unit = !config.unit ? '' : ' [' + config.unit + ']';
    return (config.label) ? this.translate.instant(config.label) + unit : config.signalName + unit;

  }

  private refreshDisplayedSignalLabels(): void {
    for (const [row, items] of this.displayedSignalsValues.entries()) {
      for (const [col, item] of items.entries()) {
        item.label = this.getDisplayedLabel(item, this.getConfigDetails(this.configuration, row, col));
      }
    }
  }

  private stopDataRetrieval() {
    this.pollingService.stop(String(this.machine.id));
  }

  private startDataRetrieval(): void {
    this.stopDataRetrieval();
    if (this.tileHasNoSignalsConfigured) {
      return;
    }
    this.computing = true;

    this.settingsService.isLiveDataStreamingEnabled().subscribe({
      next: result => {
        if (result) {
          this.pollingServiceStart();
        } else {
          this.computing = false;
        }
      }
    });
  }

  private pollingServiceStart() {
    const pollingRequestParameters: RealtimePollingRequestParameters = new RealtimePollingRequestParameters({
      machineId: this.machine.id,
      organizationId: this.machine.parentId,
      refresh: this.refreshInterval > 0 ? this.refreshInterval : this.REFRESH_INTERVAL,
      sampling: this.SAMPLING_RATE
    });

    this.pollingService.start(this.MACHINE_DATA_URL_SUFFIX, pollingRequestParameters, this.machine.id + '')
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (result) => {
        console.debug(new Date() + '-> got tile data');
        if (!result) {
          this.computing = false;
          return;
        }
        this.displayedSignalsValues = this.parseValues(result.values);
        this.trafficLight = result.trafficLight;
        this.gatewaysStatus = this.machineDetailsService.determineAggregatesGatewaysStatus(result.cloudplugs, result.missingConfiguration);
        this.gateways = this.determineGatewaysStatuses(result.cloudplugs);
        this.machineStatus = result.machineStatus;
        this.statusChangeTimestamp = result.statusChangeTimestamp;
        this.showDate = MachineComponent.isTimestampOlderThanMidnight(result.statusChangeTimestamp);
        this.gatewaysConnectorsActive = this.machineDetailsService.determineGatewayConnectorStatus(result.cloudplugs);
        this.trafficLightClass = this.machineDetailsService.determineTileTrafficLightClass(result, this.gatewaysStatus);
        this.gatewaysStatusClass = this.machineDetailsService.generateGatewaysStatusClass(this.gatewaysStatus, result.cloudplugs, true);
        this.placeholderImage = this.getImagePlaceholder(result.values);
        this.computing = false;
      },
      error: (error) => {
        console.error('Error getting dashboard data', JSON.stringify(error));
      }
    });
  }

  private determineGatewaysStatuses(gateways: Gateway[]): Gateway[] {
    if (gateways && gateways.length > 0) {
      for (const gateway of gateways) {
        gateway.computedStatus = this.gatewayStatusService.determineGatewayStatusByType(gateway);
      }
    }
    return gateways;
  }

  private getImagePlaceholder(values: any): string | undefined {
    if (!values) {
      return undefined;
    }

    const img = values.placeholderImage;
    if (!img) {
      return undefined;
    }

    if (img.length === 0) {
      return undefined;
    }

    return MachineComponent.decodeBase64ImageSourceString(img);

  }

  private translateMachineStatusIntoDescriptionLabel(): string {

    if (this.gatewaysStatus && this.gatewaysStatus === GATEWAYS_AGGREGATED_STATUS.NO_GATEWAYS) {
      return this.translate.instant('No gateways configured.');
    } else if (this.gatewaysStatus && this.gatewaysStatus === GATEWAYS_AGGREGATED_STATUS.MISCONFIGURATION) {
      return this.translate.instant('The machine configuration is missing or is incorrect.');
    } else if (this.gatewaysStatus && this.gatewaysStatus === GATEWAYS_AGGREGATED_STATUS.DISCONNECTED) {
      return this.translate.instant(' Machine is offline.');
    } else if ((this.machineStatus === MACHINE_STATUS.UNKNOWN || !this.displayedSignalsValues) && this.gatewaysConnectorsActive) {
      return this.translate.instant('Input Value Missing');
    } else if (!this.machine.chartConfig.statusIndicator || (this.machine.chartConfig.statusIndicator && !this.machine.chartConfig.statusIndicator.expressions)) {
      return this.translate.instant('Missing status indicator');
    } else if (this.gatewaysStatus && (this.gatewaysStatus === GATEWAYS_AGGREGATED_STATUS.CONNECTED || this.gatewaysStatus === GATEWAYS_AGGREGATED_STATUS.PARTIALLY_DISCONNECTED)) {
      if (this.gatewaysConnectorsActive && this.machineStatus === MACHINE_STATUS.RUNNING) {
        return this.translate.instant('Running since');
      } else if (this.gatewaysConnectorsActive && (this.machineStatus === MACHINE_STATUS.STOPPED || this.machineStatus === MACHINE_STATUS.STOPPED_BEFORE)) {
        return this.translate.instant('Stopped since');
      }
    } else if (!this.gatewaysConnectorsActive || (this.gatewaysConnectorsActive && this.machineStatus === MACHINE_STATUS.CANNOTEVALUATE)) {
      return this.translate.instant('Input Value Missing');
    }
    return this.translate.instant('Input Value Missing');
  }

  private displayMachineStatusInformation(): string {
    if (this.gatewaysConnectorsActive && (this.MACHINE_STATUS_UNAVAILABLE.indexOf(this.machineStatus) < 0)) {
      if (this.showDate) {
        return this.dateService.getMediumDate(this.statusChangeTimestamp) + '';
      } else {
        return this.dateService.getMediumTime(this.statusChangeTimestamp) + '';
      }
    }
    return '';
  }

  private focusOnLeftMenu(): void {
    this.loadingMenuItem = true;
    const id = (this.currentMachineGroupId || this.currentUser?.machineGroup) + '_' + this.machine.id;
    this.dashboardService.getItemMenuItemAndParentMenuItem(id, true).subscribe((machineMenuItem: MenuItem) => {
      this.loadingMenuItem = false;
      this.menuNotificationService.broadcastClickOnDashboard(machineMenuItem);
    });
  }

  private generateStatusEventPageMenuItem(machineMenuItem: MenuItem): MenuItem {

    const statusEventPageMenuItem: MenuItem = new MenuItem();
    statusEventPageMenuItem.pageId = this.machine.trafficLightConfiguration.statusEventPageId;
    statusEventPageMenuItem.icon = MenuItemIcon.STATUS_EVENT_PAGE;
    statusEventPageMenuItem.menuItemParent = machineMenuItem;
    statusEventPageMenuItem.id = this.machine.trafficLightConfiguration.statusEventPageMenuId;
    statusEventPageMenuItem.name = this.machine.trafficLightConfiguration.statusEventPageName;
    statusEventPageMenuItem.machineId = this.machine.id;
    statusEventPageMenuItem.organizationId = this.machine.parentId;
    statusEventPageMenuItem.type = MenuItemType.STATUS_EVENT_PAGE;
    statusEventPageMenuItem.machineCategory = this.machine.category;
    return statusEventPageMenuItem;
  }

  private setCategoryIconAndDisplayName() {
    const machineCategory = this.machineCategoryService.findMachineCategoryConfiguration(this.availableMachineCategories, this.machine);
    if (!machineCategory) {
      this.machineIcon = '';
      this.categoryDisplayName = this.machineType;
    } else {
      this.machineIcon = machineCategory.categoryIcon;
      this.categoryDisplayName = machineCategory.categoryDisplayName;
    }
  }

  private subscribe(): void {
    this.translate.onLangChange
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: ({lang}) => {
        this.portalLanguage = lang;
        this.refreshDisplayedSignalLabels();
      },
      error: error => console.log(error)
    });

    this.dashboardService.getTileConfiguration(this.machine.id)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (config) => {
        this.configuration = config;
      },
      error: error => console.log(error)
    });
  }
}
